import React from 'react';
import './Measurements.css';

interface Measurement {
  id: string;
  timestamp: string;
  weight?: number;
  height?: number;
  bodyFatPercentage?: number;
  muscleMass?: number;
  waistCircumference?: number;
  hipCircumference?: number;
  [key: string]: any;
}

interface ComparisonPanelProps {
  current: Measurement;
  previous?: Measurement;
}

export const ComparisonPanel: React.FC<ComparisonPanelProps> = ({ current, previous }) => {
  if (!previous) {
    return (
      <div className="ns-comparison-panel">
        <p className="ns-muted-text" style={{ textAlign: 'center', padding: '20px' }}>
          Primeira medição - não há dados anteriores para comparação
        </p>
      </div>
    );
  }

  const calculateDifference = (currentVal?: number, previousVal?: number): { value: number; percent: number; trend: 'up' | 'down' | 'stable' } | null => {
    if (currentVal === undefined || currentVal === null || previousVal === undefined || previousVal === null) {
      return null;
    }
    const diff = currentVal - previousVal;
    const percent = previousVal !== 0 ? (diff / previousVal) * 100 : 0;
    const trend = Math.abs(percent) < 0.1 ? 'stable' : (diff > 0 ? 'up' : 'down');
    return { value: diff, percent, trend };
  };

  const formatValue = (val?: number, unit: string = '') => {
    if (val === undefined || val === null) return '-';
    return `${val.toFixed(1)}${unit}`;
  };

  const formatDifference = (diff: { value: number; percent: number; trend: 'up' | 'down' | 'stable' }) => {
    const sign = diff.value >= 0 ? '+' : '';
    return `${sign}${diff.value.toFixed(1)} (${sign}${diff.percent.toFixed(1)}%)`;
  };

  const getTrendColor = (trend: 'up' | 'down' | 'stable', field: string) => {
    // Para peso, gordura e cintura: menor é melhor (down = bom)
    // Para músculo: maior é melhor (up = bom)
    const isNegativeGood = ['weight', 'bodyFatPercentage', 'waistCircumference'].includes(field);
    
    if (trend === 'stable') return '#64748b';
    if (isNegativeGood) {
      return trend === 'down' ? '#10b981' : '#ef4444';
    } else {
      return trend === 'up' ? '#10b981' : '#ef4444';
    }
  };

  const getTrendIcon = (trend: 'up' | 'down' | 'stable', field: string) => {
    const isNegativeGood = ['weight', 'bodyFatPercentage', 'waistCircumference'].includes(field);
    
    if (trend === 'stable') return '➡️';
    if (isNegativeGood) {
      return trend === 'down' ? '📉' : '📈';
    } else {
      return trend === 'up' ? '📈' : '📉';
    }
  };

  const comparisons = [
    { field: 'weight', label: 'Peso', unit: ' kg', current: current.weight, previous: previous.weight },
    { field: 'bodyFatPercentage', label: '% Gordura', unit: '%', current: current.bodyFatPercentage, previous: previous.bodyFatPercentage },
    { field: 'muscleMass', label: 'Massa Muscular', unit: ' kg', current: current.muscleMass, previous: previous.muscleMass },
    { field: 'waistCircumference', label: 'Cintura', unit: ' cm', current: current.waistCircumference, previous: previous.waistCircumference },
    { field: 'hipCircumference', label: 'Quadril', unit: ' cm', current: current.hipCircumference, previous: previous.hipCircumference },
  ].filter(c => c.current !== undefined && c.current !== null && c.previous !== undefined && c.previous !== null);

  if (comparisons.length === 0) {
    return (
      <div className="ns-comparison-panel">
        <p className="ns-muted-text" style={{ textAlign: 'center', padding: '20px' }}>
          Dados insuficientes para comparação
        </p>
      </div>
    );
  }

  return (
    <div className="ns-comparison-panel">
      <h3 className="ns-block-title">
        Comparação com Medição Anterior
      </h3>
      <div className="ns-comparison-grid">
        {comparisons.map(({ field, label, unit, current: curr, previous: prev }) => {
          const diff = calculateDifference(curr, prev);
          if (!diff) return null;

          const trendColor = getTrendColor(diff.trend, field);
          const trendIcon = getTrendIcon(diff.trend, field);

          return (
            <div key={field} className="ns-comparison-item">
              <div className="ns-comparison-header">
                <span className="ns-comparison-label">{label}</span>
                <span className="ns-comparison-trend" style={{ color: trendColor }}>
                  {trendIcon}
                </span>
              </div>
              <div className="ns-comparison-values">
                <div className="ns-comparison-value">
                  <span className="ns-comparison-value-label">Anterior:</span>
                  <span className="ns-comparison-value-number">{formatValue(prev, unit)}</span>
                </div>
                <div className="ns-comparison-arrow">→</div>
                <div className="ns-comparison-value">
                  <span className="ns-comparison-value-label">Atual:</span>
                  <span className="ns-comparison-value-number" style={{ color: '#0ea5e9', fontWeight: 600 }}>
                    {formatValue(curr, unit)}
                  </span>
                </div>
              </div>
              <div className="ns-comparison-diff" style={{ color: trendColor }}>
                {formatDifference(diff)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

